import { Avatar, Badge, Box, Divider, Group, Stack, Text } from '@mantine/core';
import { Users } from 'react-feather';
import { ContentCard } from '../../../components/common/ContentCard';
import type { ReflectionGroupContext } from '../../../services/api/preSessionReflection';
import {
  getParticipantDisplayName,
  getParticipantInitials,
} from '../../../utils/participantDisplayName';

const TITLE_COLOR = '#1A1A2E';
const ICON_COLOR = '#6B7280';

interface Props {
  group: ReflectionGroupContext;
  /**
   * MED-1092 — card corner radius. Defaults to 16 to match the other prep
   * cards; the peer-leader launch card passes 16 explicitly.
   */
  radius?: number | string;
}

const AVATAR_COLORS = [
  'medleyGreen',
  'warningAmber',
  'dangerRed',
  'groupAlpha',
  'groupBeta',
  'groupGamma',
  'surveyPurple',
];

/**
 * Your Group card (MED-803 AC #12).
 *
 * Renders the group name, member count, and a list of group members.
 * The current participant is flagged with a "[You]" pill.
 *
 * Avatar colors are derived deterministically from the user_id so each
 * member gets a stable color across page loads.
 */
export function YourGroupCard({ group, radius = 16 }: Props) {
  return (
    <ContentCard variant="bordered" radius={radius} padding="lg" contentGap={0}>
      <Stack gap="sm">
        <Group gap="xs" align="center">
          <Users size={18} color={ICON_COLOR} aria-hidden />
          <Text fw={600} fz={17} c={TITLE_COLOR}>
            Your Group
          </Text>
          {group.group_name && (
            <Text size="sm" c="dimmed">
              {group.group_name} · {group.member_count} members
            </Text>
          )}
        </Group>

        {group.members.length === 0 ? (
          <Text size="sm" c="dimmed" fs="italic">
            Group members are not yet assigned.
          </Text>
        ) : (
          <Stack gap={0}>
            {group.members.map((m, idx) => {
              // MED-1162 — label + initials by preferred-or-first name (AC1-3),
              // keeping the existing "First L." last-initial format.
              const displayName = getParticipantDisplayName(m.preferred_name, m.first_name);
              return (
              <Box key={m.user_id}>
                {idx > 0 && <Divider my="xs" />}
                <Group justify="space-between" align="center" py={4}>
                  <Group gap="sm" wrap="nowrap">
                    <Avatar size="sm" radius="xl" color={pickColor(m.user_id)}>
                      {getParticipantInitials(m.preferred_name, m.first_name, m.last_initial)}
                    </Avatar>
                    <Text size="sm">
                      {displayName} {m.last_initial}.
                    </Text>
                  </Group>
                  {m.is_current_participant && (
                    <Badge
                      color="blue"
                      variant="light"
                      radius="sm"
                      size="sm"
                      styles={{
                        label: { textTransform: 'none', fontWeight: 500 },
                      }}
                    >
                      You
                    </Badge>
                  )}
                </Group>
              </Box>
              );
            })}
          </Stack>
        )}
      </Stack>
    </ContentCard>
  );
}

function pickColor(userId: string): string {
  let hash = 0;
  for (let i = 0; i < userId.length; i++) {
    hash = (hash * 31 + userId.charCodeAt(i)) >>> 0;
  }
  return AVATAR_COLORS[hash % AVATAR_COLORS.length];
}
